import { action, makeObservable, observable } from 'mobx';
import SlidesModel from './SlidesModel';

export interface IElement {
  elementType: string;
  elementId: number;
  elementPosition: {
    bottom: number;
    left: number;
    right: number;
    top: number;
    height: number;
    width: number;
    x: number;
    y: number;
  };
  isFirstFoundElement: boolean;
  isVisible: boolean;
  areaName: string;
  templateId: string | null;
}

export type ISiteBuilderMap = Map<number, IElement>;

class EditorStateMachine {
  /**
   * Elements that are currently on the page
   */
  public elements: ISiteBuilderMap = new Map();

  /**
   * When the element is hovered, this is the id of the element
   */
  public hoveredElementId: number | null = null;

  /**
   * When a locked element is hovered, this is the type of the element
   */
  public hoveredLockedElementType: string | null = null;

  /**
   * When the element data is being regenerated, this is the id of the element
   */
  public elementRegeneratingDataId: number | null = null;

  /**
   * When a section is clicked/selected for editing
   */
  public selectedSectionId: number | null = null;

  /**
   * Position of the selected section element (from iframe)
   */
  public selectedSectionPosition: DOMRect | null = null;

  /**
   * Edit mode for the selected section (when "Edit elements" is clicked)
   */
  public isSectionInEditMode: boolean = false;


  public editModeSectionId: number | null = null;

  /**
   * Editable elements in the selected section (when in edit mode)
   */
  public editableSectionElements: Array<{
    elementId: number;
    elementType: string;
    areaName: string;
    elementPosition: DOMRect;
    cmsTemplateId: string | null;
    cmsElementId: string;
  }> = [];

  /**
   * Locked elements in the selected section (when in edit mode)
   */
  public lockedSectionElements: Array<{
    elementType: string;
    label: string;
    elementPosition: DOMRect;
    elementId?: number; // Optional element ID for position tracking
  }> = [];

  /**
   * Section ID that was dismissed by ESC or click outside.
   * Hover won't re-select this section until mouse leaves it first.
   */
  public dismissedSectionId: number | null = null;

  public stateMachine = {
    currentState: 'idle',
    states: {
      idle: {
        onElementHover: this.onElementHover.bind(this),
        onSlideAnimationStart: this.onSlideAnimationStart.bind(this),
        onButtonHovered: this.onButtonHovered.bind(this),
        onIdle: this.onIdle.bind(this)
      },
      'element-hovered': {
        onElementHover: this.onElementHover.bind(this),
        onEditText: this.onEditText.bind(this),
        onEditPicture: this.onEditPicture.bind(this),
        onSlideAnimationStart: this.onSlideAnimationStart.bind(this),
        onIdle: this.onIdle.bind(this),
        onButtonHovered: this.onButtonHovered.bind(this)
      },
      'element-start-regenerating-data': {
        onElementFinishRegeneratingData:
          this.onElementFinishRegeneratingData.bind(this)
      },
      'edit-text': {
        onFinishEditText: this.onFinishEditText.bind(this)
      },
      'finish-edit-text': {
        onElementHover: this.onElementHover.bind(this)
      },
      'edit-picture': {
        onFinishEditPicture: this.onFinishEditPicture.bind(this)
      },
      'finish-edit-picture': {
        onElementHover: this.onElementHover.bind(this)
      },
      'slide-change-start': {
        onSlideAnimationFinished: this.onSlideAnimationFinished.bind(this),
        onSlideAnimationStartAfterDelay:
          this.onSlideAnimationStartAfterDelay.bind(this)
      },
      'button-hovered': {
        onButtonHoveredEnd: this.onButtonHoveredEnd.bind(this),
        onEditText: this.onEditText.bind(this),
        onEditPicture: this.onEditPicture.bind(this),
        onElementStartRegeneratingData:
          this.onElementStartRegeneratingData.bind(this)
      }
    }
  };

  constructor() {
    makeObservable(this, {
      elements: observable,
      stateMachine: observable,
      hoveredElementId: observable,
      hoveredLockedElementType: observable,
      elementRegeneratingDataId: observable,
      selectedSectionId: observable,
      selectedSectionPosition: observable,
      isSectionInEditMode: observable,
      editModeSectionId: observable,
      editableSectionElements: observable,
      lockedSectionElements: observable,
      dismissedSectionId: observable,
      initializeElements: action,
      transition: action,
      resetElements: action,
      selectSection: action,
      deselectSection: action,
      toggleSectionEditMode: action,
      setEditableSectionElements: action,
      setLockedSectionElements: action,
      setHoveredLockedElement: action,
      updateSectionPosition: action,
      clearDismissedSection: action
    });
  }

  private onButtonHovered() {
    return 'button-hovered';
  }

  private onButtonHoveredEnd() {
    return 'idle';
  }

  private onSlideAnimationStartAfterDelay(props: { elementId: any }) {
    SlidesModel.removeSliderTransition(props.elementId);

    return 'slide-change-start';
  }

  private onSlideAnimationStart(props: {
    elementPosition: any;
    elementId: any;
  }) {
    this.hoveredElementId = null;

    SlidesModel.setSliderTransition(props.elementId, {
      position: props.elementPosition
    });

    return 'slide-change-start';
  }

  private onIdle() {
    this.hoveredElementId = null;

    return 'idle';
  }

  private onSlideAnimationFinished(props: {
    newPositions: any;
    elementId: any;
  }) {
    SlidesModel.removeSliderTransition(props.elementId);

    return 'idle';
  }

  private onElementHover(props: { elementId: number; elementPosition: any }) {
    // update element position
    if (this.elements.has(props.elementId)) {
      this.elements.get(props.elementId).elementPosition =
        props.elementPosition;
    }

    // update hovered element
    this.hoveredElementId = props.elementId;

    return 'element-hovered';
  }

  private onEditText(props: { elementId: number }) {
    this.hoveredElementId = null;

    return 'edit-text';
  }

  private onEditPicture(props: { elementId: number }) {
    return 'edit-picture';
  }

  private onFinishEditText(props: { newPositions }) {
    this.elements = props.newPositions;

    return 'finish-edit-text';
  }

  private onFinishEditPicture() {
    return 'finish-edit-picture';
  }

  private onElementStartRegeneratingData(props: { elementId: number }) {
    this.hoveredElementId = null;
    this.elementRegeneratingDataId = props.elementId;

    return 'element-start-regenerating-data';
  }

  private onElementFinishRegeneratingData() {
    this.elementRegeneratingDataId = null;

    return 'idle';
  }

  public transition(event, params) {
    const nextState =
      this.stateMachine.states[this.stateMachine.currentState][event];
    if (nextState) {
      this.stateMachine.currentState = nextState(params);
    } else {
      console.warn(
        `Invalid transition from ${this.stateMachine.currentState} on event ${event}`
      );
    }
  }

  public initializeElements(elements: ISiteBuilderMap) {
    this.elements = elements;
  }

  public resetElements() {
    this.elements = new Map();
  }

  public selectSection(sectionId: number, sectionPosition: DOMRect) {
    // If clicking the same section, keep the edit mode state
    const isSameSection = this.selectedSectionId === sectionId;
    this.selectedSectionId = sectionId;
    
    // Always update position to keep overlay in sync during scroll
    this.selectedSectionPosition = sectionPosition;
  }

  public deselectSection() {
    const wasInEditMode = this.isSectionInEditMode;
    const previousSectionId = this.selectedSectionId;
    
    this.selectedSectionId = null;
    this.selectedSectionPosition = null;
    this.isSectionInEditMode = false;
    this.editModeSectionId = null;
    this.editableSectionElements = [];
    this.lockedSectionElements = [];
    
    // Remember which section was dismissed to prevent immediate re-selection on hover
    this.dismissedSectionId = previousSectionId;
    
    // If we were in edit mode, notify to remove navigation styles
    if (wasInEditMode) {
      try {
        window.dispatchEvent(
          new CustomEvent('wizard:exit-edit-mode-cleanup', {
            detail: { removeNavigationStyles: true }
          })
        );
      } catch (e) {
        // ignore in non-browser contexts
      }
    }
  }

  public updateSectionPosition(position: DOMRect) {
    this.selectedSectionPosition = position;
  }

  public clearDismissedSection() {
    this.dismissedSectionId = null;
  }

  public toggleSectionEditMode() {
    this.isSectionInEditMode = !this.isSectionInEditMode;

    // Set or clear the edit mode section ID
    if (this.isSectionInEditMode) {
      this.editModeSectionId = this.selectedSectionId;
    } else {
      this.editModeSectionId = null;
    }

    try {
      // Notify other parts of the app (and the iframe messenger) about edit mode change
      window.dispatchEvent(
        new CustomEvent('wizard:section-edit-mode-changed', {
          detail: { isInEditMode: this.isSectionInEditMode }
        })
      );
    } catch (e) {
      // ignore in non-browser contexts
    }
  }

  public setEditableSectionElements(
    elements: Array<{
      elementId: number;
      elementType: string;
      areaName: string;
      elementPosition: DOMRect;
      cmsTemplateId: string | null;
      cmsElementId: string;
    }>
  ) {
    this.editableSectionElements = elements;
  }

  public setLockedSectionElements(
    elements: Array<{
      elementType: string;
      label: string;
      elementPosition: DOMRect;
      elementId?: number;
    }>
  ) {
    this.lockedSectionElements = elements;
  }

  public setHoveredLockedElement(elementType: string | null) {
    this.hoveredLockedElementType = elementType;
  }
}

export default new EditorStateMachine();
